import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { FaFlag } from 'react-icons/fa'

import { Box, Text } from '@chakra-ui/react'

import type { ColorScheme } from '@hivewatch/dark'
import { IncidentPriorityIcon, TableCheckbox, tokenVar } from '@hivewatch/dark'

import { formatDateTime, formatTimeDistance } from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'
import { getIncidentPriorityDisplayName } from '@/utils/incidents'

import type { DarkIncidentColumn } from '../types'
import {
  FacilityCell,
  GuardCell,
  OperatorCell,
  SourceCell,
  StatusCell,
  TooltipTarget,
} from './cells'

interface UseDarkIncidentColumnsIProps {
  colorScheme: ColorScheme
  /** Whether the multi-select checkbox column is shown (bulk actions available). */
  showCheckbox: boolean
  /** Whether the AI-flag column is shown (aiOperator flag on). */
  showFlag: boolean
  /** Whether Facility/Floor lose sortability. */
  isFacilityFloorSortDisabled: boolean
  /**
   * Whether Priority Queue's pinned two-key sort is active: Priority becomes a
   * fixed, non-interactive desc sort (badge 1); Date is the only remaining
   * clickable sort (badge 2); every other previously-sortable column loses
   * sortability for the duration.
   */
  isPriorityQueueActive: boolean
  isAllSelected: boolean
  isSomeSelected: boolean
  onToggleAll: () => void
  isRowSelected: (id: string) => boolean
  onToggleRow: (id: string) => void
}

/**
 * Builds the rebranded Incidents table columns (ENG-30677), following the column
 * model shared by the other new-design tables. Column order: checkbox? · flag? ·
 * Priority · Status · INC ID · Date · Name · Facility · Floor · Source ·
 * Operator · Guard — Status sits immediately right of Priority.
 */
export const useDarkIncidentColumns = ({
  colorScheme,
  showCheckbox,
  showFlag,
  isFacilityFloorSortDisabled,
  isPriorityQueueActive,
  isAllSelected,
  isSomeSelected,
  onToggleAll,
  isRowSelected,
  onToggleRow,
}: UseDarkIncidentColumnsIProps): DarkIncidentColumn[] => {
  const { t } = useTranslation()

  return useMemo<DarkIncidentColumn[]>(() => {
    const checkboxColumn: DarkIncidentColumn = {
      key: 'bulkActionCheckbox',
      header: (
        <TableCheckbox
          checked={isAllSelected}
          data-testid='incidentsPage_table_selectAllCheckbox'
          indeterminate={isSomeSelected}
          onChange={onToggleAll}
        />
      ),
      style: { minWidth: '48px', maxWidth: '48px', align: 'center' },
      // Fill the whole cell (incl. the 16px padding) and stop propagation, so a
      // mis-click near the checkbox toggles selection instead of opening the
      // drawer — parity with the legacy `ignoreClicksToCell` guard. The dark
      // TableCell clips overflow, so the expanded box stays within the cell.
      cell: (row) => (
        <Box
          alignItems='center'
          alignSelf='stretch'
          display='flex'
          justifyContent='center'
          mx='-16px'
          onClick={(e) => e.stopPropagation()}
          px='16px'
        >
          <TableCheckbox
            checked={isRowSelected(row.id)}
            data-testid={`incidentsPage_table_selectRowCheckbox_${row.id}`}
            onChange={() => onToggleRow(row.id)}
          />
        </Box>
      ),
    }

    const flagColumn: DarkIncidentColumn = {
      key: 'isFlagged',
      header: '',
      style: { minWidth: '56px', maxWidth: '56px', align: 'center' },
      cell: (row) =>
        row.isFlagged ? (
          <TooltipTarget
            colorScheme={colorScheme}
            dataTestId='incidentsPage_table_flaggedCell'
            label={t('incidents:col_ai_flagged')}
          >
            <FaFlag
              color={tokenVar(
                colorScheme === 'light'
                  ? 'colors.light.icon.danger'
                  : 'colors.icon.danger'
              )}
              size={18}
            />
          </TooltipTarget>
        ) : null,
    }

    const dataColumns: DarkIncidentColumn[] = [
      {
        key: 'priority',
        header: t('incidents:col_priority'),
        // Priority is pinned (fixed desc, non-interactive) while Priority Queue
        // is active - the manually-controlled TableHeaderCell mode (sortOrder,
        // no sortColumn) renders that without registering it as clickable.
        ...(isPriorityQueueActive
          ? { sortOrder: 'desc' as const, sortBadge: 1 as const }
          : { sortColumn: 'priority' }),
        style: { minWidth: '88px', maxWidth: '96px', align: 'center' },
        cell: (row) => (
          <TooltipTarget
            colorScheme={colorScheme}
            dataTestId='incidentsPage_table_priorityCell'
            label={
              row.priority
                ? tVar(t, 'incidents:col_priority_tooltip', {
                    priority: getIncidentPriorityDisplayName(row.priority),
                  })
                : t('incidents:priority_no_priority')
            }
          >
            <IncidentPriorityIcon
              colorScheme={colorScheme}
              priority={row.priority}
            />
          </TooltipTarget>
        ),
      },
      {
        key: 'incidentStatusOwner',
        header: t('common:status'),
        ...(!isPriorityQueueActive && { sortColumn: 'incidentStatusOwner' }),
        style: { minWidth: '140px', maxWidth: '200px', flexGrow: 1 },
        cell: (row) => (
          <StatusCell
            colorScheme={colorScheme}
            incidentStatusOwner={row.incidentStatusOwner}
          />
        ),
      },
      {
        key: 'displayId',
        header: t('common:inc_id'),
        ...(!isPriorityQueueActive && { sortColumn: 'displayId' }),
        style: { minWidth: '120px', maxWidth: '160px', flexGrow: 1 },
        cell: (row) => (
          <TooltipTarget colorScheme={colorScheme} label={row.displayId}>
            <Text
              color={`text.primary.${colorScheme}`}
              data-testid='incidentsPage_table_displayIdCell'
              isTruncated
            >
              {row.displayId}
            </Text>
          </TooltipTarget>
        ),
      },
      {
        key: 'date',
        header: t('common:date'),
        sortColumn: 'date',
        ...(isPriorityQueueActive && { sortBadge: 2 as const }),
        style: { minWidth: '160px', maxWidth: '220px', flexGrow: 1 },
        cell: (row) => (
          <TooltipTarget
            colorScheme={colorScheme}
            label={`${formatDateTime(row.date)} • ${formatTimeDistance(
              row.date
            )}`}
          >
            <Text
              color={`text.primary.${colorScheme}`}
              data-testid='incidentsPage_table_dateCell'
              isTruncated
            >
              {formatDateTime(row.date)}
            </Text>
          </TooltipTarget>
        ),
      },
      {
        key: 'incidentName',
        header: t('common:name'),
        ...(!isPriorityQueueActive && { sortColumn: 'incidentName' }),
        style: { minWidth: '160px', maxWidth: '320px', flexGrow: 1 },
        cell: (row) => (
          <TooltipTarget colorScheme={colorScheme} label={row.incidentName}>
            <Text
              color={`text.primary.${colorScheme}`}
              data-testid='incidentsPage_table_incidentNameCell'
              isTruncated
            >
              {row.incidentName}
            </Text>
          </TooltipTarget>
        ),
      },
      {
        key: 'facility',
        header: t('common:facility'),
        ...(!isFacilityFloorSortDisabled && { sortColumn: 'facility' }),
        style: { minWidth: '160px', maxWidth: '280px', flexGrow: 1 },
        cell: (row) => (
          <FacilityCell colorScheme={colorScheme} facility={row.facility} />
        ),
      },
      {
        key: 'floorName',
        header: t('common:floor'),
        ...(!isFacilityFloorSortDisabled && { sortColumn: 'floorName' }),
        style: { minWidth: '100px', maxWidth: '160px', flexGrow: 1 },
        cell: (row) => (
          <TooltipTarget colorScheme={colorScheme} label={row.floorName}>
            <Text
              color={`text.primary.${colorScheme}`}
              data-testid='incidentsPage_table_floorCell'
              isTruncated
            >
              {row.floorName}
            </Text>
          </TooltipTarget>
        ),
      },
      {
        key: 'source',
        header: t('incidents:col_source'),
        style: { minWidth: '140px', maxWidth: '240px', flexGrow: 1 },
        cell: (row) => (
          <SourceCell colorScheme={colorScheme} source={row.source} />
        ),
      },
      {
        key: 'operator',
        header: t('common:operator'),
        ...(!isPriorityQueueActive && { sortColumn: 'operator' }),
        style: { minWidth: '96px', maxWidth: '120px', align: 'center' },
        cell: (row) => (
          <OperatorCell colorScheme={colorScheme} operator={row.operator} />
        ),
      },
      {
        key: 'guard',
        header: t('common:guard'),
        ...(!isPriorityQueueActive && { sortColumn: 'guard' }),
        style: { minWidth: '96px', maxWidth: '120px', align: 'center' },
        cell: (row) => (
          <GuardCell colorScheme={colorScheme} incident={row.guard} />
        ),
      },
    ]

    return [
      ...(showCheckbox ? [checkboxColumn] : []),
      ...(showFlag ? [flagColumn] : []),
      ...dataColumns,
    ]
  }, [
    colorScheme,
    showCheckbox,
    showFlag,
    isFacilityFloorSortDisabled,
    isPriorityQueueActive,
    isAllSelected,
    isSomeSelected,
    onToggleAll,
    isRowSelected,
    onToggleRow,
    t,
  ])
}
